import { Sparkles, Zap } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

const MIN_MANUAL_HOURS = 0;
const MAX_MANUAL_HOURS = 150;

const formatManualHours = (hours) => `${hours} ${hours === 1 ? "hour" : "hours"}`;

const useAnimatedNumber = (target, { duration = 480, initialValue = 0 } = {}) => {
  const [displayValue, setDisplayValue] = useState(initialValue);
  const currentValueRef = useRef(initialValue);

  useEffect(() => {
    let frameId;
    let startTime;
    const startValue = currentValueRef.current;
    const delta = target - startValue;

    if (Math.abs(delta) < 0.001) {
      currentValueRef.current = target;
      setDisplayValue(target);
      return undefined;
    }

    const tick = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easedProgress = 1 - (1 - progress) ** 3;
      const nextValue = startValue + delta * easedProgress;
      currentValueRef.current = nextValue;
      setDisplayValue(nextValue);

      if (progress < 1) {
        frameId = window.requestAnimationFrame(tick);
      } else {
        currentValueRef.current = target;
        setDisplayValue(target);
      }
    };

    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [duration, target]);

  return displayValue;
};

export const InstitutionalRoiCalculator = ({ onSeatCheckoutStart }) => {
  const [monthlyLeads, setMonthlyLeads] = useState(48);
  const [manualFollowUpHours, setManualFollowUpHours] = useState(42);

  const result = useMemo(() => {
    const leadFactor = monthlyLeads / 100;
    const effortFactor = manualFollowUpHours / MAX_MANUAL_HOURS;
    const workloadScore = Math.min(1, leadFactor * effortFactor);
    const projectedSignatureGrowth = Math.round(20 + workloadScore * 15);
    const reclaimedHours = manualFollowUpHours * 0.85;

    return {
      projectedSignatureGrowth,
      reclaimedHours,
      manualHoursLabel: formatManualHours(manualFollowUpHours),
    };
  }, [manualFollowUpHours, monthlyLeads]);

  const animatedProjectedSignatureGrowth = useAnimatedNumber(result.projectedSignatureGrowth, { duration: 440, initialValue: 0 });
  const animatedReclaimedHours = useAnimatedNumber(result.reclaimedHours, { duration: 620, initialValue: 0 });

  return (
    <div className="rounded-[34px] border border-[#D4AF37]/24 bg-[linear-gradient(135deg,rgba(212,175,55,0.24)_0%,rgba(212,175,55,0.06)_40%,rgba(255,255,255,0.04)_100%)] p-[1px] shadow-[0_30px_90px_rgba(0,25,53,0.22)]" data-testid="institutional-roi-calculator-shell">
      <div className="rounded-[33px] bg-[linear-gradient(135deg,rgba(3,26,52,0.94)_0%,rgba(7,29,56,0.9)_48%,rgba(35,58,89,0.7)_100%)] p-6 backdrop-blur-xl" data-testid="institutional-roi-calculator-card">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]" data-testid="institutional-roi-kicker">Institutional ROI</p>
        <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-semibold leading-tight text-[#F8F3E8]" data-testid="institutional-roi-heading">Calculate Your Alpha Edge</h2>
        <p className="mt-4 max-w-2xl text-sm leading-8 text-[#E6DCC4]" data-testid="institutional-roi-subheading">See how Institutional Automation converts your current lead flow into a profit multiplier.</p>

        <div className="mt-8 grid gap-5">
          <div className="rounded-[24px] border border-[#D4AF37]/16 bg-[rgba(255,255,255,0.04)] px-5 py-4" data-testid="institutional-roi-monthly-leads-control">
            <div className="flex items-center justify-between gap-4">
              <label className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]" htmlFor="institutional-roi-monthly-leads">Monthly Lead Volume</label>
              <span className="text-sm font-semibold text-[#F8F3E8]" data-testid="institutional-roi-monthly-leads-value">{monthlyLeads}</span>
            </div>
            <input
              className="mt-4 h-2 w-full cursor-pointer accent-[#D4AF37]"
              data-testid="institutional-roi-monthly-leads-slider"
              id="institutional-roi-monthly-leads"
              max="100"
              min="0"
              onChange={(event) => setMonthlyLeads(Number(event.target.value))}
              type="range"
              value={monthlyLeads}
            />
            <div className="mt-2 flex justify-between text-[11px] uppercase tracking-[0.14em] text-[#E6DCC4]/70">
              <span>0</span>
              <span>100</span>
            </div>
          </div>

          <div className="rounded-[24px] border border-[#D4AF37]/16 bg-[rgba(255,255,255,0.04)] px-5 py-4" data-testid="institutional-roi-follow-up-control">
            <div className="flex items-center justify-between gap-4">
              <label className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]" htmlFor="institutional-roi-follow-up-time">Monthly Manual Follow-up</label>
              <span className="text-sm font-semibold text-[#F8F3E8]" data-testid="institutional-roi-follow-up-value">{result.manualHoursLabel}</span>
            </div>
            <input
              className="mt-4 h-2 w-full cursor-pointer accent-[#D4AF37]"
              data-testid="institutional-roi-follow-up-slider"
              id="institutional-roi-follow-up-time"
              max={String(MAX_MANUAL_HOURS)}
              min={String(MIN_MANUAL_HOURS)}
              onChange={(event) => setManualFollowUpHours(Number(event.target.value))}
              step="1"
              type="range"
              value={manualFollowUpHours}
            />
            <div className="mt-2 flex justify-between text-[11px] uppercase tracking-[0.14em] text-[#E6DCC4]/70">
              <span>0 hours</span>
              <span>150 hours</span>
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-[28px] border border-[#D4AF37]/20 bg-[linear-gradient(135deg,rgba(255,250,241,0.08)_0%,rgba(212,175,55,0.08)_100%)] px-5 py-6" data-testid="institutional-roi-result-card">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]" data-testid="institutional-roi-result-label">Potential Alpha Gain</p>
          <h3 className="mt-3 text-4xl font-semibold leading-tight text-[#D4AF37]" data-testid="institutional-roi-result-value">{Math.round(animatedProjectedSignatureGrowth)}% Projected Signature Growth</h3>
          <p className="mt-4 text-sm leading-8 text-[#F8F3E8]" data-testid="institutional-roi-result-description">High-volume lead intake paired with heavy monthly follow-up creates the strongest automation upside. Instant routing and AI briefings can turn your current {result.manualHoursLabel.toLowerCase()} of manual effort into a materially faster, higher-trust closing system.</p>
        </div>

        <div className="mt-5 rounded-[24px] border border-[#D4AF37]/16 bg-[rgba(255,255,255,0.04)] px-5 py-5" data-testid="institutional-roi-reclaimed-hours-card">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]" data-testid="institutional-roi-reclaimed-hours-label">Institutional Time Recovery</p>
          <div className="mt-3 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/24 bg-[rgba(212,175,55,0.12)]" data-testid="institutional-roi-reclaimed-hours-icon-shell">
              <Zap className="h-4 w-4 text-[#D4AF37]" data-testid="institutional-roi-reclaimed-hours-bolt-icon" />
            </div>
            <div>
              <p className="text-2xl font-semibold leading-tight text-[#D4AF37]" data-testid="institutional-roi-reclaimed-hours-value">
                Reclaim up to {animatedReclaimedHours.toFixed(1)} Hours/Month for High-Value Closings.
              </p>
              <p className="mt-3 text-sm leading-7 text-[#E6DCC4]" data-testid="institutional-roi-reclaimed-hours-description">Institutional automation is modeled to reclaim 85% of your manual monthly follow-up workload and reallocate that time into faster, higher-conviction conversations.</p>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[24px] border border-[#D4AF37]/14 bg-[rgba(255,255,255,0.03)] px-5 py-5">
          <p className="max-w-xl text-sm font-semibold uppercase tracking-[0.14em] text-[#F8F3E8]" data-testid="institutional-roi-cta-text">Stop Leaking Leads. Capture Your Alpha Now.</p>
          <button className="gold-button" data-testid="institutional-roi-cta-button" onClick={onSeatCheckoutStart} type="button">
            Secure My Founding Seat <Sparkles className="ml-2 h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};